import React from "react";
import { Card, TileLabel, chipClass } from "./index";
import {
  PERIOD_RETURN_FEE_NOTE,
  buildPeriodReturnView,
  feeBasisLabel,
  periodReturnFraming,
} from "../../lib/navPerformance";
import type { NavReconciliation } from "../../lib/navTypes";

/**
 * Estimated period return since the administrator's last official strike.
 *
 * ONE component, mounted on Risk / Portfolio / Signals, so the same estimated return
 * figure can never disagree between two screens. All arithmetic and all period-labeling
 * lives in lib/navPerformance.ts (unit-tested); this file only renders.
 *
 * DISPLAY RULES (see docs/HANDOFF_PERF_RECON_MTD_TILE.md section 4):
 *  - "MTD" appears only when the strike genuinely is the prior month-end; otherwise the
 *    period is stated literally ("since 06-30-2026").
 *  - The anchor is always on the tile face — never an implicit period.
 *  - Direction is carried by sign, never by color (the system-wide rule in cockpit/delta.tsx).
 *    A gain does not turn this tile green.
 *  - Nothing is rendered when the inputs are missing; no value is ever defaulted to zero.
 *  - This tile does NOT read intraday day P&L. The change since the strike already
 *    contains today's move whenever nav_basis_includes_today is true, so adding day P&L
 *    would re-introduce the double-count fixed 2026-08-06.
 *
 * DRAFT — the copy here needs CIO / compliance sign-off before it is visible to anyone
 * outside the CIO. Figures are estimates; NAV Fund Services is the sole official source.
 */

const fmtPctSigned = (x?: number | null, digits = 2): string => {
  if (typeof x !== "number" || !Number.isFinite(x)) return "—";
  const pct = x * 100;
  const sign = pct > 0 ? "+" : pct < 0 ? "−" : "";
  return `${sign}${Math.abs(pct).toFixed(digits)}%`;
};

const fmtUsdSigned = (x?: number | null): string => {
  if (typeof x !== "number" || !Number.isFinite(x)) return "—";
  const sign = x > 0 ? "+" : x < 0 ? "−" : "";
  return `${sign}$${Math.abs(x).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export const PeriodReturnTile: React.FC<{
  reconciliation?: NavReconciliation | null;
  /** Set on a page that also shows the official figure elsewhere, to avoid repeating it. */
  hideOfficial?: boolean;
  className?: string;
}> = ({ reconciliation, hideOfficial = false, className }) => {
  const v = buildPeriodReturnView(reconciliation);

  // The framing sentence must describe the basis ACTUALLY rendered: flow-ledger wording
  // only when the backend supplied flow fields, legacy paired wording otherwise — static
  // copy claiming a flow subtraction the figure does not carry would be a reassurance
  // defect in the flattering direction.
  const hint =
    `${periodReturnFraming(v)} ${PERIOD_RETURN_FEE_NOTE} ` +
    (v.flowState !== null
      ? "Numerator is the IBKR book's move since the strike, less attested capital flows from the operator " +
        "flow ledger (dbo.CapitalFlows); the off-broker overlay is carried inside the anchor. When flows are " +
        "unattested or too large for the beginning-NAV denominator, the percentage is withheld and dollars stay."
      : "Numerator is the IBKR book's move since the strike; the off-broker overlay is carried inside the anchor.");

  if (!v.available) {
    return (
      <Card className={className}>
        <TileLabel hint={hint}>{v.periodLabel}</TileLabel>
        <div className="text-lg font-semibold mt-1 leading-none" style={{ color: "var(--text-muted)" }}>
          —
        </div>
        <div className="text-[11px] mt-1 leading-5" style={{ color: "var(--text-muted)" }}>
          {v.unavailableReason}
        </div>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <TileLabel hint={hint}>{v.periodLabel}</TileLabel>

      {/* Net of the modeled management-fee accrual is the headline; gross sits beside it
          so the fee effect is visible rather than buried. When the percentage is
          SUPPRESSED (flows unattested or too large for the beginning-NAV denominator),
          dollars take the headline and the "% withheld" chip carries the reason — a
          missing figure sends the reader to the official package; a wrong one does not. */}
      {/* When the calibration record supports a drift correction (3+ scored months,
          same sign, ≥0.5pp — see trailing_bias_pp backend-side), the CORRECTED figure
          leads and the raw figure stays visible right beside it: a correction is shown,
          never silently substituted. Otherwise this renders exactly as before. */}
      {/* Co-headline pair (CIO request 2026-09-02): the before-incentive estimate AND
          the anticipated official net, side by side at equal weight, so the "where
          might NAV land" read takes one glance. The right figure is ALWAYS labelled
          modelled and renders only in clean/adjusted flow states — a suppressed or
          unattested month must not carry a confident-looking official projection. */}
      <div className="mt-1 flex items-end gap-x-6 gap-y-2 flex-wrap">
        <div>
          {/* When the pair renders, BOTH columns carry a mini-label: on narrow tiles
              the row wraps, and an unlabelled left figure directly above the right
              column's label would let the higher, before-incentive number read as
              the official projection — the flattering misread, exactly the one this
              surface exists to prevent. */}
          {v.estOfficialNetPct !== null ? (
            <div
              className="text-[10px] uppercase tracking-wider leading-none mb-1"
              style={{ color: "var(--text-muted)" }}
            >
              net est. · before incentive
            </div>
          ) : null}
          <div className="text-lg font-semibold leading-none" style={{ color: "var(--text-primary)" }}>
            {v.percentSuppressed
              ? fmtUsdSigned(v.netUsd)
              : fmtPctSigned(v.correctedNetPct ?? v.netPct)}
            <span className="text-[11px] font-normal ml-1.5" style={{ color: "var(--text-muted)" }}>
              {v.percentSuppressed
                ? "% withheld"
                : v.correctedNetPct !== null
                ? `raw ${fmtPctSigned(v.netPct)} · ${fmtUsdSigned(v.netUsd)}`
                : fmtUsdSigned(v.netUsd)}
            </span>
          </div>
        </div>
        {v.estOfficialNetPct !== null ? (
          <div
            title={
              (v.estOfficialHaircutApplied
                ? "Flat 20% incentive model applied to the RAW net figure (before any drift correction) — the official Net ROR is net of the administrator's accrued incentive, so this is the like-for-like read on where the official month might land. Not the exact carry: the 8% hurdle with full catch-up ran ~23% effective in strong months, and per-investor HWMs are not modeled."
                : "No incentive accrues on a losing month, so no haircut is applied — this matches the raw net figure. Where prior months accrued incentive, the official has landed somewhat better than the estimate in down months (accrual reversals, ~0.4-0.8pp on the 2026 record) — that credit is deliberately not modeled, and with nothing accrued to reverse there is no such cushion.") +
              " Modelled and unofficial — NAV Fund Services is the sole official source. The calibration line below reports the MEASURED carry each strike."
            }
          >
            <div
              className="text-[10px] uppercase tracking-wider leading-none mb-1"
              style={{ color: "var(--text-muted)" }}
            >
              est. official net · modelled
            </div>
            <div className="text-lg font-semibold leading-none" style={{ color: "var(--text-primary)" }}>
              ≈ {fmtPctSigned(v.estOfficialNetPct)}
            </div>
          </div>
        ) : null}
      </div>
      {v.correctedNetPct !== null && v.driftBiasPp !== null ? (
        <div
          className="text-[11px] mt-0.5"
          style={{ color: "var(--text-muted)" }}
          title="Empirical drift correction: the estimator's error vs the official Net ROR has run one-directional for 3+ consecutive scored months, so the median of those errors is subtracted. It disappears automatically if the drift stops being persistent, material and same-signed."
        >
          drift-corrected {v.driftBiasPp >= 0 ? "−" : "+"}
          {Math.abs(v.driftBiasPp).toFixed(2)}pp (3-mo measured record)
        </div>
      ) : null}

      <div className="text-[11px] mt-1 leading-5" style={{ color: "var(--text-muted)" }}>
        <div>
          {v.percentSuppressed
            ? // Two suppressed flavours, and the dollars mean different things: when the
              // flow was QUANTIFIED (too large for a %), the dollars are flow-adjusted
              // and sound; when the window is UNATTESTED, the dollars are the raw change
              // and may include the entire flow — say so on the face, not in a tooltip.
              `${fmtUsdSigned(v.grossUsd)} before fees${v.dollarsFlowAdjusted ? "" : " · incl. any unattested flows"} · ${feeBasisLabel(v)}`
            : `${fmtPctSigned(v.grossPct)} (${fmtUsdSigned(v.grossUsd)}) before fees · ${feeBasisLabel(v)}`}
        </div>
        {v.flowState === "adjusted" ? (
          <div title="Attested capital flows from the operator flow ledger, subtracted from the numerator so admitted capital does not read as performance.">
            adjusted for {fmtUsdSigned(v.flowAdjustmentUsd)} of capital flows ({v.flowSource ?? "manual"} ledger)
            {typeof v.feeAddBackUsd === "number"
              ? ` · ${fmtUsdSigned(v.feeAddBackUsd)} fee cash added back (paid via sweep; cost carried in the fee line)`
              : ""}
          </div>
        ) : null}
        {/* LP-structure-neutral per-share view: units from the strike (official NAV ÷
            official NAVPS) grown by attested flows issued at strike NAVPS, so this
            return is flow-immune by construction. No per-investor data involved. */}
        {typeof v.estNavpsNow === "number" && typeof v.navpsAtStrike === "number" ? (
          <div title="Estimated NAV per share — mirrors the administrator's unit framework (units grow with attested flows at strike NAVPS), so the per-share return is flow-immune by construction. Before each LP's own incentive/HWM, which is per-investor and not modeled.">
            est. NAVPS {v.estNavpsNow.toLocaleString(undefined, { style: "currency", currency: "USD" })} vs{" "}
            {v.navpsAtStrike.toLocaleString(undefined, { style: "currency", currency: "USD" })} at strike
            {typeof v.estNavpsRetPct === "number" ? ` (${fmtPctSigned(v.estNavpsRetPct / 100)})` : ""}
          </div>
        ) : null}
        <div>
          {v.anchorLabel}
          {" · estimated, unofficial"}
        </div>

        {v.caveats.length ? (
          <div className="flex flex-wrap gap-1 mt-1">
            {v.caveats.map((c) => (
              <span key={c.badge} className={chipClass("warning")} title={c.detail}>
                {c.badge}
              </span>
            ))}
          </div>
        ) : null}

        {!hideOfficial ? (
          <div className="mt-1.5 pt-1.5" style={{ borderTop: "1px solid var(--card-border)" }}>
            {v.official.available ? (
              <span title="NAV Fund Services official Net ROR — net of fees. The only investor-reportable return here.">
                Official (NAV Fund Services, net):{" "}
                <strong style={{ color: "var(--text-primary)" }}>{fmtPctSigned(v.official.mtdPct)}</strong> month
                {" · YTD "}
                <strong style={{ color: "var(--text-primary)" }}>{fmtPctSigned(v.official.ytdPct)}</strong>
                {v.official.asOf ? ` · struck ${v.official.asOf}` : ""}
              </span>
            ) : (
              <span title={v.official.withheldReason}>Official return: withheld — {v.official.withheldReason}</span>
            )}
            {/* Monthly self-calibration: the just-closed month is a free, exact test of
                this tile's estimator, scored the day each strike lands. Sign carries
                direction (+ = the estimate read HIGH), and the attribution tail keeps
                the identity visible on the face: err = measured carry + residual
                (Aug-2026: +1.08 = +0.86 + 0.22). Carry is measured via the NAVPS wedge;
                the residual approximates the Refinitiv-vs-IBKR mark-gap change. */}
            {typeof v.calibration?.err_net_pp === "number" ? (
              <div
                className="mt-0.5"
                title="Self-calibration, updated each official strike: the flow-adjusted, mgmt-fee-netted estimate vs the official Net ROR for the just-closed month. The official is NET of the administrator's accrued incentive reallocation (a monthly LP→GP capital move, verified vs the Aug-2026 package), so the wedge between NAVPS return (gross of carry) and the official MEASURES the carry; what remains after subtracting it approximates the Refinitiv-vs-IBKR settlement mark-gap change — random-signed, ~0.2pp median and up to ~0.45pp observed, attributed rather than corrected."
              >
                Calibration {v.calibration.month_ending ? `(${v.calibration.month_ending.slice(0, 5)})` : ""}: estimate
                read{" "}
                <strong style={{ color: "var(--text-primary)" }}>
                  {v.calibration.err_net_pp >= 0 ? "+" : "−"}
                  {Math.abs(v.calibration.err_net_pp).toFixed(2)}pp {v.calibration.err_net_pp >= 0 ? "high" : "low"}
                </strong>{" "}
                ({fmtPctSigned((v.calibration.est_net_pct ?? 0) / 100)} est vs{" "}
                {fmtPctSigned((v.calibration.official_net_pct ?? 0) / 100)} official)
                {typeof v.calibration.carry_drag_pp === "number" &&
                typeof v.calibration.residual_after_carry_pp === "number" ? (
                  <>
                    {" · measured carry "}
                    {v.calibration.carry_drag_pp >= 0 ? "+" : "−"}
                    {Math.abs(v.calibration.carry_drag_pp).toFixed(2)}pp
                    {" · residual "}
                    {v.calibration.residual_after_carry_pp >= 0 ? "+" : "−"}
                    {Math.abs(v.calibration.residual_after_carry_pp).toFixed(2)}pp (≈ mark gap)
                  </>
                ) : null}
              </div>
            ) : v.calibration?.note ? (
              <div className="mt-0.5" style={{ opacity: 0.8 }}>
                Calibration: {v.calibration.note}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
};

export default PeriodReturnTile;
